/**
 * Per-audience organisation profile → the `<organization>` prompt layer.
 *
 * The product identity (`<crucible>` in ./identity.ts) never names a
 * customer or a customer's systems; everything organisation-specific lives
 * here. Which block a user gets depends on who they are, not on the tenant:
 * this deployment also hosts beta-customer and partner users on 3PH
 * accounts, and they must not be told about 3PH's studies, systems or plans.
 *
 * - 3PH staff (capability `context:3ph_internal`) get the full 3PH profile.
 * - Everyone else gets the external block, naming only their own
 *   organisation (from an `org:<slug>` grant) or none.
 *
 * The prompt is not the security boundary — tool gates are. This layer only
 * keeps the model from volunteering what a user's tools would not show them.
 */

export interface ConnectedSystem {
  /** System category, e.g. "EDC/CTMS", "Quality management". */
  category: string
  /** What is connected, in one sentence or phrase. */
  detail: string
}

export interface OrganizationProfile {
  /** The organisation Crucible is deployed for. */
  name: string
  /** Optional parent organisation and how `name` relates to it. */
  parent?: {
    name: string
    /** How `name` relates to the parent, e.g. "CRO subsidiary". */
    relationship: string
  }
  /** What the organisation does — paragraphs separated by a blank line. */
  description: string
  /** Systems wired into Crucible for this organisation. */
  connectedSystems: readonly ConnectedSystem[]
  /** Further internal context, each rendered as its own paragraph. */
  notes?: readonly string[]
  /** Closing instruction (e.g. look live data up rather than recall it). */
  closingInstruction: string
}

/** The 3PH profile: 3PH Alliance and its CRO subsidiary HRCRS. Staff only. */
export const THREEPH_ORGANIZATION: OrganizationProfile = {
  name: 'HRCRS',
  parent: { name: '3PH Alliance', relationship: 'CRO subsidiary' },
  description: `HRCRS runs real-world-evidence research in cardiac electrophysiology:
long-running multicentre registries, usually sponsor-funded, with nested
sub-studies, alongside investigator-initiated and pre-market (IDE) studies.
Its portfolio spans catheter and pulsed-field ablation for atrial
fibrillation and ventricular arrhythmias, cardiac mapping (including
AI-guided mapping), left atrial appendage closure, post-ablation rhythm
monitoring and procedural adjuncts. It also offers full-service CRO work:
site management, monitoring, data management, biostatistics and medical
writing.`,
  connectedSystems: [
    {
      category: 'EDC/CTMS',
      detail:
        '3PH Cloud (in service) and 3PH Forward, also called Anvil (its replacement, in development). Study builds and test plans target 3PH Cloud today.',
    },
    { category: 'Quality management', detail: 'Qualio.' },
    { category: 'CRM', detail: 'HubSpot.' },
    { category: 'Physician surveys', detail: 'SurveyMonkey.' },
    {
      category: 'STRIDE',
      detail:
        "3PH's read-only reference database. It holds the study library (ODM 1.3.1 structures from past 3PH studies: events, forms, item groups, items, codelists, conditions and range checks, plus estimands, objectives and endpoints) and the MDD, the Master Data Dictionary (canonical data elements by CDISC domain, mappings from study items to them, and ACC NCDR registry element definitions). The CDISC, SNOMED CT, ICD-10-CM and LOINC tables behind the standards tools live in the same database.",
    },
  ],
  notes: [
    `For MDD questions, prefer the dedicated tools: stride_mdd_items for the
canonical items (filter by CDISC domain), stride_mdd_mappings for which study
items map to them, and stride_ncdr for NCDR registry element definitions.
Fall back to stride_query only for what they don't cover.`,
    `3PH has demonstrated EMR integration through Redox and the Epic and
Cerner sandboxes, in its cohort-feasibility work. CarePath AI, which answers
CRF questions from a patient's medical record with cited evidence and human
review, is in development and not available in Crucible yet. Never take
patient records into chat to stand in for it.`,
    `Crucible has beta-customer and partner users. Don't name them or discuss
their work.`,
  ],
  closingInstruction: `For the current list of studies, codebooks, documents or deals, look them
up with your tools. Don't rely on memory for names, counts or statuses.`,
}

/** Render a profile into the `<organization>` prompt block. */
export function renderOrganizationContext(profile: OrganizationProfile): string {
  const deployedFor = profile.parent
    ? `You are deployed for ${profile.parent.name} and its ${profile.parent.relationship} ${profile.name}.`
    : `You are deployed for ${profile.name}.`

  const parts = [deployedFor, profile.description]
  if (profile.connectedSystems.length > 0) {
    parts.push(
      [
        'Systems connected for this organisation:',
        ...profile.connectedSystems.map((s) => `- ${s.category}: ${s.detail}`),
      ].join('\n'),
    )
  }
  parts.push(...(profile.notes ?? []))
  parts.push(profile.closingInstruction)

  return `<organization>\n${parts.join('\n\n')}\n</organization>`
}

/**
 * The `<organization>` block for a user outside 3PH. Names only their own
 * organisation, never 3PH's studies, systems, customers or plans.
 */
export function renderExternalOrganizationContext(orgName?: string): string {
  const who = orgName
    ? `You are assisting a user from ${orgName}.`
    : 'You are assisting a user from outside the organisation that operates this deployment.'
  return `<organization>
${who} Crucible is provided to them by 3PH Alliance.

Share only what your tools return for this user. Don't describe 3PH's own
studies, systems, data, customers, partners or plans, and don't infer them
from tool names or errors. If asked, say it's outside what you can share here.
</organization>`
}

/** The `<organization>` block for 3PH staff. */
export const INTERNAL_ORGANIZATION_CONTEXT = renderOrganizationContext(THREEPH_ORGANIZATION)
